import { toSupportingDocumentMeta } from './supporting-documents';
import {
  Investment,
  InvestmentCurrency,
  InvestmentPaymentMethod,
  InvestmentStage,
  InvestmentStageType,
  StageTimelineEntry,
  SubscriptionDocumentRef,
} from './types';

// ==================== BE contract mirror ====================

/**
 * Mirrors the investment context's `SubscriptionStatus` (see the BE enum of the
 * same name). Shorter than the FE's `InvestmentStage`: the Kanban-only
 * pre-subscription stages and `Redeemed` have no BE counterpart.
 */
export const SubscriptionStatus = {
  SubscriptionStarted: 'SubscriptionStarted',
  EligibilitySubmitted: 'EligibilitySubmitted',
  AgreementSigned: 'AgreementSigned',
  DataSubmissionAcknowledged: 'DataSubmissionAcknowledged',
  PaymentMethodSelected: 'PaymentMethodSelected',
  FundsSent: 'FundsSent',
  FundsReceived: 'FundsReceived',
  Invested: 'Invested',
  Cancelled: 'Cancelled',
} as const;

export type SubscriptionStatusType = (typeof SubscriptionStatus)[keyof typeof SubscriptionStatus];

export interface WireAttestationDto {
  dateSent: string;
  sendingBank: string;
  /** Decimal string, normalized by the backend ('250000.50' arrives as '250000.5'). */
  amount: string;
  reference: string;
}

/**
 * The whole wizard state of one subscription, as `SubscriptionDto` serializes
 * it. Every step the investor has not reached reports `null` rather than being
 * omitted, so "not done yet" is distinguishable from "field missing".
 */
export interface SubscriptionDto {
  id: string;
  investorUserId: string;
  fundId: string;
  status: SubscriptionStatusType;
  amount: string;
  denomination: string;
  eligibilitySubmittedAt: string | null;
  agreementDocumentId: string | null;
  agreementSignedAt: string | null;
  supportingDocuments: SubscriptionDocumentRef[];
  dataAcknowledgmentCompletedAt: string | null;
  paymentMethod: InvestmentPaymentMethod | null;
  paymentMethodSelectedAt: string | null;
  /** Issued by the backend in the `INV-XXXXXXXXXX` form; never composed here. */
  paymentReference: string | null;
  wireAttestation: WireAttestationDto | null;
  fundsSentAt: string | null;
  fundsReceivedAt: string | null;
  cancelledAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Everything needed to re-embed the Documenso widget for an unsigned agreement. */
export interface AgreementSessionDto {
  documentId: string;
  signingToken: string | null;
  signingUrl: string | null;
  title: string | null;
}

/**
 * The wizard's single load-time read (`GET /bff/v1/investing-views/wizard-resume`).
 * `subscription` is null when the investor has never started one in this fund or
 * cancelled the last attempt; `agreementSession` is present only while an
 * agreement document exists and awaits signature.
 *
 * The fund contributes its signing template and nothing more — payment
 * instructions come from the public fund read (`toFundPaymentConfig`), which is
 * the only place the fund's wire details are served.
 */
export interface WizardResumeResponse {
  subscription: SubscriptionDto | null;
  fundDocumensoTemplateId: string | null;
  agreementSession: AgreementSessionDto | null;
}

// ==================== Projection ====================

/**
 * The backend and the wizard name the same steps identically, apart from
 * cancellation: the FE has no `Cancelled` stage and files an abandoned
 * subscription under `Rejected` alongside the fund-admin rejections.
 */
const STAGE_BY_STATUS: Record<SubscriptionStatusType, InvestmentStageType> = {
  [SubscriptionStatus.SubscriptionStarted]: InvestmentStage.SubscriptionStarted,
  [SubscriptionStatus.EligibilitySubmitted]: InvestmentStage.EligibilitySubmitted,
  [SubscriptionStatus.AgreementSigned]: InvestmentStage.AgreementSigned,
  [SubscriptionStatus.DataSubmissionAcknowledged]: InvestmentStage.DataSubmissionAcknowledged,
  [SubscriptionStatus.PaymentMethodSelected]: InvestmentStage.PaymentMethodSelected,
  [SubscriptionStatus.FundsSent]: InvestmentStage.FundsSent,
  [SubscriptionStatus.FundsReceived]: InvestmentStage.FundsReceived,
  [SubscriptionStatus.Invested]: InvestmentStage.Invested,
  [SubscriptionStatus.Cancelled]: InvestmentStage.Rejected,
};

/**
 * A status this build has never heard of can only come from a newer backend.
 * Resuming at step one is the recoverable answer — the investor can walk
 * forward again and the backend rejects any step it has already taken — whereas
 * throwing would leave the wizard with nothing to render at all.
 */
const toStage = (status: SubscriptionStatusType): InvestmentStageType =>
  STAGE_BY_STATUS[status] ?? InvestmentStage.SubscriptionStarted;

/**
 * The wizard keeps no stage history of its own, so the timeline is rebuilt from
 * the per-step timestamps. They are written in wizard order, which is why the
 * entries need no sorting.
 */
const toStageTimeline = (dto: SubscriptionDto): StageTimelineEntry[] => {
  const steps: [InvestmentStageType, string | null][] = [
    [InvestmentStage.SubscriptionStarted, dto.createdAt],
    [InvestmentStage.EligibilitySubmitted, dto.eligibilitySubmittedAt],
    [InvestmentStage.AgreementSigned, dto.agreementSignedAt],
    [InvestmentStage.DataSubmissionAcknowledged, dto.dataAcknowledgmentCompletedAt],
    [InvestmentStage.PaymentMethodSelected, dto.paymentMethodSelectedAt],
    [InvestmentStage.FundsSent, dto.fundsSentAt],
    [InvestmentStage.FundsReceived, dto.fundsReceivedAt ?? null],
    [InvestmentStage.Rejected, dto.cancelledAt],
  ];

  return steps
    .filter((step): step is [InvestmentStageType, string] => step[1] !== null)
    .map(([stage, at]) => ({ stage, at }));
};

/**
 * Reshapes a subscription into the `Investment` shape the wizard components,
 * the Portfolio's Pending Assets rows and the fund page's in-progress banner
 * all render.
 *
 * Two groups of `Investment` fields have no counterpart on a subscription and
 * stay empty: post-wizard settlement (`fundsReceived`, `unitsIssued`), which the
 * fund admin records elsewhere; and FE-local wizard UX state
 * (`eligibilitySubStepsCompleted`, `acknowledgments`), which lives in component
 * state.
 *
 * Attached documents arrive twice over, for two different jobs:
 * `subscriptionDocuments` is the verbatim set the replace-PUT resends, and
 * `supportingDocuments` is the categorized view the upload slots render and the
 * completeness gate reads.
 *
 * `fundId` is an argument because callers usually hold the fund from the route;
 * it is the same value the payload carries.
 */
export const projectSubscriptionToInvestment = (
  dto: SubscriptionDto,
  fundId: string = dto.fundId,
): Investment => ({
  id: dto.id,
  fundId,
  investorId: dto.investorUserId,
  stage: toStage(dto.status),
  amount: Number(dto.amount),
  currency: dto.denomination as InvestmentCurrency,
  paymentMethod: dto.paymentMethod,
  paymentReference: dto.paymentReference,
  wireConfirmation: dto.wireAttestation
    ? {
        date: dto.wireAttestation.dateSent,
        bank: dto.wireAttestation.sendingBank,
        amount: Number(dto.wireAttestation.amount),
        reference: dto.wireAttestation.reference,
      }
    : null,
  fundsReceived: null,
  unitsIssued: null,
  createdAt: dto.createdAt,
  lastActivityAt: dto.updatedAt,
  stageTimeline: toStageTimeline(dto),
  subscriptionDocuments: dto.supportingDocuments,
  supportingDocuments: toSupportingDocumentMeta(dto.supportingDocuments),
  agreementDocumentId: dto.agreementDocumentId ?? undefined,
});
